import { Suspense, lazy, useEffect } from 'react'
import {
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigationType,
} from 'react-router-dom'
import {
  AppShell,
  Button,
  ErrorState,
  OfflineBanner,
  Spinner,
  useViewportHeight,
  type NavDestination,
} from '@modulus/ui'
import {
  BookOpen,
  CalendarDays,
  CircleUser,
  House,
  LogOut,
  MessageCircle,
  ScanLine,
} from 'lucide-react'
import { useSession } from './lib/session'
import { useConnectivity } from './lib/connectivity'
import { Auth } from './features/auth/Auth'
import { Onboarding } from './features/auth/Onboarding'

/**
 * Every screen is lazy.
 *
 * Auth is the exception: it is what an unauthenticated visitor sees, and making
 * them wait on a second round-trip is the most obviously wrong place for a spinner.
 */
const Home = lazy(() => import('./features/home/Home').then((m) => ({ default: m.Home })))
const Planner = lazy(() =>
  import('./features/planner/Planner').then((m) => ({ default: m.Planner })),
)
const Timetable = lazy(() =>
  import('./features/timetable/Timetable').then((m) => ({ default: m.Timetable })),
)
const Tests = lazy(() => import('./features/tests/Tests').then((m) => ({ default: m.Tests })))
const TestRunner = lazy(() =>
  import('./features/tests/TestRunner').then((m) => ({ default: m.TestRunner })),
)
const TestHistory = lazy(() =>
  import('./features/tests/TestHistory').then((m) => ({ default: m.TestHistory })),
)
const QuickRevise = lazy(() =>
  import('./features/tests/QuickRevise').then((m) => ({ default: m.QuickRevise })),
)
const Solve = lazy(() => import('./features/solve/Solve').then((m) => ({ default: m.Solve })))
const SolveDetail = lazy(() => import('./features/solve/Solve').then((m) => ({ default: m.SolveDetail })))
const Chats = lazy(() => import('./features/chat/Chats').then((m) => ({ default: m.Chats })))
const Profile = lazy(() =>
  import('./features/profile/Profile').then((m) => ({ default: m.Profile })),
)
const Analytics = lazy(() =>
  import('./features/profile/Analytics').then((m) => ({ default: m.Analytics })),
)
const Leaderboard = lazy(() =>
  import('./features/profile/Leaderboard').then((m) => ({ default: m.Leaderboard })),
)
const Subjects = lazy(() =>
  import('./features/profile/Subjects').then((m) => ({ default: m.Subjects })),
)
const Plan = lazy(() => import('./features/profile/Plan').then((m) => ({ default: m.Plan })))
const StudyPreferences = lazy(() =>
  import('./features/profile/StudyPreferences').then((m) => ({ default: m.StudyPreferences })),
)

/** The native app's five persistent student destinations, in the same order. */
const DESTINATIONS: NavDestination[] = [
  { to: '/', label: 'Home', icon: <House size={18} /> },
  { to: '/chat', label: 'Chats', icon: <MessageCircle size={18} /> },
  { to: '/solve', label: 'Solve', icon: <ScanLine size={18} /> },
  { to: '/calendar', label: 'Calendar', icon: <CalendarDays size={18} /> },
  { to: '/tests', label: 'Tests', icon: <BookOpen size={18} /> },
]

/**
 * Start each screen at the top.
 *
 * On a phone the document is the scroller, and it carried its offset from one
 * route to the next — tapping Calendar from halfway down Profile landed you
 * halfway down Calendar, or at its end if the new page was shorter. A POP is
 * left alone: going back should return to where you were.
 */
function ScrollToTop() {
  const { pathname } = useLocation()
  const navigationType = useNavigationType()

  useEffect(() => {
    if (navigationType === 'POP') return
    window.scrollTo(0, 0)
    // The laptop layout scrolls the main pane, not the document.
    document.querySelector('.mx-main')?.scrollTo(0, 0)
  }, [pathname, navigationType])

  return null
}

export default function App() {
  const { loading, isSignedIn, profile, needsOnboarding, error, reload, signOut } = useSession()
  const { online, queued } = useConnectivity()
  const location = useLocation()

  // Publishes the visual viewport, so a pane sized to the screen means the screen
  // the keyboard actually leaves behind.
  useViewportHeight()

  if (loading) {
    return <Spinner label="Loading Modulus" />
  }

  if (!isSignedIn) {
    return <Auth />
  }

  if (error) {
    return (
      <div style={{ padding: 'var(--space-l)' }}>
        <ErrorState error={error} onRetry={reload} />
      </div>
    )
  }

  // A brand-new account has no curriculum, and every other screen reads from one.
  if (needsOnboarding) {
    return <Onboarding />
  }

  const name = profile?.display_name?.trim() || profile?.email || 'You'

  const routes = (
    <Suspense fallback={<Spinner />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/calendar" element={<Planner />} />
        <Route path="/planner" element={<Navigate to="/calendar" replace />} />
        <Route path="/timetable" element={<Timetable />} />
        <Route path="/tests" element={<Tests />} />
        <Route path="/tests/run" element={<TestRunner />} />
        <Route path="/tests/history" element={<TestHistory />} />
        <Route path="/tests/history/:attemptId" element={<TestHistory />} />
        <Route path="/revise" element={<QuickRevise />} />
        <Route path="/solve" element={<Solve />} />
        <Route path="/solve/:itemId" element={<SolveDetail />} />
        <Route path="/chat" element={<Chats />} />
        <Route path="/chat/:chatId" element={<Chats />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/profile/analytics" element={<Analytics />} />
        <Route path="/profile/leaderboard" element={<Leaderboard />} />
        <Route path="/profile/subjects" element={<Subjects />} />
        <Route path="/profile/study-preferences" element={<StudyPreferences />} />
        <Route path="/profile/plan" element={<Plan />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )

  // A test is deliberately a focused workspace: switching destinations mid-test
  // is too easy to do by accident, and a fixed app sidebar makes it feel like a
  // page rather than the assessment in progress.
  if (location.pathname === '/tests/run') {
    return (
      <main className="mx-main mx-main-immersive" id="main-content" tabIndex={-1}>
        <ScrollToTop />
        {!online || queued > 0 ? <OfflineBanner queued={queued} /> : null}
        {routes}
      </main>
    )
  }

  return (
    <AppShell
      destinations={DESTINATIONS}
      renderLink={(destination, props) => (
        // `aria-current` comes from NavLink, and the CSS keys off it rather than
        // off a class — so the styling and the announced state cannot disagree.
        <NavLink
          key={destination.to}
          to={destination.to}
          end={destination.to === '/'}
          className={props.className}
        >
          {props.children}
        </NavLink>
      )}
      banner={!online || queued > 0 ? <OfflineBanner queued={queued} /> : undefined}
      footer={
        <div className="mx-stack" style={{ gap: 'var(--space-s)' }}>
          <div className="mx-row" style={{ flexWrap: 'nowrap' }}>
            <NavLink to="/profile" className="mx-account-link" aria-label="Open your profile and settings">
              <CircleUser size={18} aria-hidden="true" />
              <span>{name}</span>
            </NavLink>
            <Button variant="ghost" size="sm" onClick={() => void signOut()} aria-label="Sign out">
              <LogOut size={15} />
            </Button>
          </div>
        </div>
      }
    >
      <ScrollToTop />
      {routes}
    </AppShell>
  )
}
